import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import { Box, IconButton, Stack, TextField, useTheme } from "@mui/material";
import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  type PaginationState,
  useReactTable,
} from "@tanstack/react-table";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import type { ModuleWithPartnerStatusListItemDto } from "Infrastructure/Api/Api";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { useSetPartnerModulesMutation } from "Partners/Api/Mutations/useSetPartnerModulesMutation";
import { usePartnerModulesQuery } from "Partners/Api/Queries/usePartnerModulesQuery";
import { BlTanstackTable } from "Shared/DataTable/BlTanstackTable";
import { BlModal } from "Shared/Dialogs/BlModal";

interface EditAvailableModulesModalProps {
  isOpen: boolean;
  onClose: () => void;
  partnerID?: string;
}

const columnHelper = createColumnHelper<ModuleWithPartnerStatusListItemDto>();

export const EditAvailableModulesModal = ({
  isOpen,
  onClose,
  partnerID,
}: EditAvailableModulesModalProps): React.ReactNode => {
  const { t } = useResource();
  const theme = useTheme();

  const [searchQuery, setSearchQuery] = useState<string>("");
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 5,
  });
  const [localEnabledIDs, setLocalEnabledIDs] = useState<Set<string>>(
    new Set(),
  );

  const { data: modulesData, isLoading } = usePartnerModulesQuery(
    isOpen ? partnerID : undefined,
  );
  const setPartnerModulesMutation = useSetPartnerModulesMutation();

  useEffect(() => {
    if (modulesData) {
      setLocalEnabledIDs(
        new Set(
          modulesData.items
            .filter((m) => m.isEnabledForPartner)
            .map((m) => m.moduleID),
        ),
      );
    }
  }, [modulesData]);

  const filteredModules = useMemo(() => {
    const modules = modulesData?.items ?? [];
    if (!searchQuery) return modules;
    const query = searchQuery.toLowerCase();
    return modules.filter((module) =>
      module.name.toLowerCase().includes(query),
    );
  }, [modulesData, searchQuery]);

  const handleToggle = useCallback((moduleID: string) => {
    setLocalEnabledIDs((prev) => {
      const next = new Set(prev);
      if (next.has(moduleID)) {
        next.delete(moduleID);
      } else {
        next.add(moduleID);
      }
      return next;
    });
  }, []);

  const handleSave = async () => {
    if (!partnerID) return;
    try {
      await setPartnerModulesMutation.mutateAsync({
        partnerID,
        moduleIDs: [...localEnabledIDs],
      });
      toast.success(
        t(Resources.Partners.EditAvailableModulesModal.SuccessMessage),
      );
      handleClose();
    } catch {
      // error handled by mutation (auto-toast via processResponse)
    }
  };

  const handleClose = () => {
    setSearchQuery("");
    setLocalEnabledIDs(new Set());
    onClose();
  };

  const columns = useMemo(
    () => [
      columnHelper.accessor("name", {
        header: t(Resources.Partners.EditAvailableModulesModal.ModuleName),
        enableSorting: false,
        cell: (info) => <Box>{info.getValue()}</Box>,
      }),
      columnHelper.accessor("type", {
        header: t(Resources.Partners.EditAvailableModulesModal.BaseModule),
        enableSorting: false,
        cell: (info) => (
          <Box>
            {t(
              Resources.Modules.ModuleTypes[
                info.getValue() as keyof typeof Resources.Modules.ModuleTypes
              ],
            )}
          </Box>
        ),
      }),
      columnHelper.display({
        id: "action",
        header: "",
        enableSorting: false,
        meta: { maxWidth: 50, justifySelf: "end" },
        cell: (info) => {
          const moduleID = info.row.original.moduleID;
          const isEnabled = localEnabledIDs.has(moduleID);
          return (
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
              }}
            >
              <IconButton
                size="small"
                onClick={() => handleToggle(moduleID)}
                sx={{
                  color: isEnabled ? theme.palette.success.main : "inherit",
                }}
              >
                {isEnabled ? <CheckIcon /> : <AddIcon />}
              </IconButton>
            </Box>
          );
        },
      }),
    ],
    [t, handleToggle, localEnabledIDs, theme],
  );

  const table = useReactTable({
    data: filteredModules,
    columns,
    getRowId: (row) => row.moduleID,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    state: { pagination },
    onPaginationChange: setPagination,
  });

  return (
    <BlModal
      isOpen={isOpen}
      title={t(Resources.Partners.EditAvailableModulesModal.Title)}
      onCancel={handleClose}
      onConfirm={handleSave}
      cancelButtonText={t(Resources.Common.Close)}
      confirmButtonText={t(Resources.Partners.EditAvailableModulesModal.Save)}
      isLoading={setPartnerModulesMutation.isPending}
      maxWidth="600px"
    >
      <Box>
        <Stack
          direction="row"
          spacing={Spacing.S}
          sx={{ marginBottom: Spacing.M }}
        >
          <Box sx={{ flex: 1 }}>
            <TextField
              fullWidth
              placeholder={t(
                Resources.Partners.EditAvailableModulesModal.SearchPlaceholder,
              )}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              size="small"
              sx={{
                "& .MuiInputBase-root": {
                  backgroundColor: (theme) => theme.colors.backgroundTile,
                  height: "32px",
                },
                "& .MuiInputBase-input": {
                  height: "32px",
                  padding: (theme) =>
                    `${theme.spacing(0.5)} ${theme.spacing(1.5)}`,
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: (theme) => theme.colors.backgroundTile,
                },
                "& .MuiInputBase-root:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: (theme) => theme.colors.backgroundTile,
                },
                "& .MuiInputBase-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                  {
                    borderColor: (theme) => theme.colors.backgroundTile,
                  },
              }}
            />
          </Box>
        </Stack>
        <BlTanstackTable table={table} isLoading={isLoading} />
      </Box>
    </BlModal>
  );
};
